/**
 * Health score z rozbiciem na składowe.
 *
 * Sam wynik „61" nie mówi, co naprawić — a to jest jedyny powód, dla którego
 * ta metryka istnieje. Składowa bez danych ma pasiaste tło i nie udaje zera:
 * produkt bez kampanii reklamowych nie ma LTV/CAC i nie może być za to karany.
 */

import { LABELS, percent, ratio } from "../lib/format";
import type { Health } from "../lib/types";
import { Chip } from "./ui";

const PERCENT_COMPONENTS = new Set([
  "mrr_growth",
  "customer_churn",
  "revenue_churn",
  "net_revenue_retention",
  "trial_conversion",
  "activation_rate",
  "organic_growth",
]);

function renderValue(name: string, value: number) {
  if (PERCENT_COMPONENTS.has(name)) return percent(value, 1);
  if (name === "ltv_cac") return ratio(value, 1);
  return String(value);
}

export function HealthPanel({ health }: { health: Health | null }) {
  if (!health) {
    return <p className="dim">Za mało danych, żeby policzyć wynik.</p>;
  }

  const tone = health.score >= 70 ? "good" : health.score >= 55 ? "warn" : "crit";
  const entries = Object.entries(health.components).sort((a, b) => b[1].weight - a[1].weight);

  return (
    <div>
      <div className="health__top">
        <span className="health__score mono">{health.score.toFixed(0)}</span>
        <span className="health__grade">{health.grade}</span>
        <Chip tone={tone}>
          {health.score >= 70 ? "zdrowy" : health.score >= 55 ? "do obserwacji" : "wymaga uwagi"}
        </Chip>
      </div>

      <p className="card__note" style={{ marginTop: "0.5rem" }}>
        Pewność {percent(health.confidence, 0)} — tyle wagi składowych udało się policzyć.
      </p>

      <div className="health__components">
        {entries.map(([name, component]) => {
          const missing = component.score === null;
          return (
            <div
              key={name}
              className={missing ? "health__row health__row--missing" : "health__row"}
              title={component.reason ?? undefined}
            >
              <span className="muted">{LABELS.healthComponent[name] ?? name}</span>
              <span className="health__meter">
                {!missing && (
                  <i
                    className="health__fill"
                    style={{ width: `${Math.max(component.score ?? 0, 2)}%` }}
                  />
                )}
              </span>
              <span className="health__value">
                {missing
                  ? "brak"
                  : component.value === null
                    ? "—"
                    : renderValue(name, component.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
